import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { 
  Button, 
  Typography, 
  Box, 
  CircularProgress,
  LinearProgress
} from '@material-ui/core';
import { 
  Videocam, 
  Stop, 
  PlayArrow, 
  Pause,
  FiberManualRecord,
  CheckCircle,
  CameraAlt,
  Mic,
  CloudUpload,
  Visibility
} from '@material-ui/icons';
import swal from 'sweetalert';
import firebase from 'firebase/app';
import 'firebase/storage';
import './VideoRecorder.css';

// Video compression function - reduces video size by re-encoding with lower quality/bitrate
// Runs in background, non-blocking with timeout protection
const compressVideo = async (videoBlob) => {
  return new Promise((resolve) => {
    // Skip compression for very small videos (< 2MB) - not worth the time
    if (videoBlob.size < 2 * 1024 * 1024) {
      console.log('ℹ️ Video is already small (<2MB), skipping compression');
      resolve(videoBlob);
      return;
    }
    
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    
    let videoUrl = null;
    let timeoutId = null;
    let animationFrameId = null;
    
    // Set timeout to prevent hanging (max 30 seconds for compression)
    timeoutId = setTimeout(() => {
      console.warn('⚠️ Compression timeout (30s), using original video');
      cleanup();
      resolve(videoBlob);
    }, 30000);
    
    const cleanup = () => {
      if (videoUrl) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {}
        videoUrl = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      video.src = '';
      video.onloadedmetadata = null;
      video.onplay = null;
      video.onended = null;
      video.onerror = null;
    };
    
    video.onloadedmetadata = () => {
      try {
      // Set canvas dimensions (reduce resolution for compression)
      // Max width: 1280px, max height: 720px, maintain aspect ratio
      const maxWidth = 1280;
      const maxHeight = 720;
      let width = video.videoWidth;
      let height = video.videoHeight;
      
      // Calculate new dimensions maintaining aspect ratio
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
      
      canvas.width = width;
      canvas.height = height;
      
        // Use MediaRecorder to re-encode with lower bitrate
        const stream = canvas.captureStream(30); // 30 FPS
        let mediaRecorder;
        
        try {
          mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'video/webm;codecs=vp9',
            videoBitsPerSecond: 1000000 // 1 Mbps (lower bitrate = smaller file)
          });
        } catch (e) {
          // Fallback to default codec if vp9 not supported
          console.warn('VP9 not supported, using default codec');
          mediaRecorder = new MediaRecorder(stream);
        }
        
        const compressedChunks = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            compressedChunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          clearTimeout(timeoutId);
          const compressedBlob = new Blob(compressedChunks, { type: 'video/webm' });
          
          // Validate compressed blob
          if (!compressedBlob || compressedBlob.size === 0 || compressedChunks.length === 0) {
            console.warn('⚠️ Compressed blob is empty, using original');
            cleanup();
            resolve(videoBlob);
            return;
          }
          
          // Only use compressed if it's actually smaller (at least 10% reduction)
          if (compressedBlob.size < videoBlob.size * 0.9 && compressedBlob.size > 0) {
            cleanup();
            resolve(compressedBlob);
          } else {
            // If compression didn't help, use original
            cleanup();
            resolve(videoBlob);
          }
        };
        
        mediaRecorder.onerror = (error) => {
          console.error('MediaRecorder error during compression:', error);
          clearTimeout(timeoutId);
          cleanup();
          resolve(videoBlob); // Fallback to original
        };
        
        // Start recording from canvas
        try {
          mediaRecorder.start();
        } catch (e) {
          console.error('Failed to start MediaRecorder:', e);
          clearTimeout(timeoutId);
          cleanup();
          resolve(videoBlob);
          return;
        }
        
        // Draw video frames to canvas
        const drawFrame = () => {
          if (!video.paused && !video.ended && video.readyState >= 2) {
            try {
              ctx.drawImage(video, 0, 0, width, height);
              animationFrameId = requestAnimationFrame(drawFrame);
            } catch (e) {
              console.error('Error drawing frame:', e);
            }
          }
        };
        
        video.onplay = () => {
          drawFrame();
        };
        
        // Play video to capture frames
        video.play().catch(err => {
          console.error('Error playing video for compression:', err);
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          clearTimeout(timeoutId);
          cleanup();
          resolve(videoBlob); // Fallback to original
        });
        
        // Stop recording when video ends
        video.onended = () => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          setTimeout(() => {
            try {
              if (mediaRecorder) {
                // Request final data before stopping
                if (mediaRecorder.state === 'recording') {
                  mediaRecorder.requestData();
                }
                // Wait a bit more for data to be available
                setTimeout(() => {
                  if (mediaRecorder.state !== 'inactive') {
                    mediaRecorder.stop();
                  }
                }, 100);
              }
            } catch (e) {
              console.error('Error stopping MediaRecorder:', e);
              cleanup();
              resolve(videoBlob);
            }
          }, 300); // Delay to ensure last frame is captured
        };
      } catch (error) {
        console.error('Error in compression setup:', error);
        clearTimeout(timeoutId);
        cleanup();
        resolve(videoBlob);
      }
    };
    
    video.onerror = (error) => {
      console.error('Video loading error during compression:', error);
      clearTimeout(timeoutId);
      cleanup();
      resolve(videoBlob); // Fallback to original
    };
    
    // Load video
    try {
      videoUrl = URL.createObjectURL(videoBlob);
      video.src = videoUrl;
      video.load();
    } catch (error) {
      console.error('Error creating object URL for compression:', error);
      clearTimeout(timeoutId);
      cleanup();
      resolve(videoBlob);
    }
  });
};

const VideoRecorder = forwardRef(({ 
  question, 
  answer, 
  onAnswerChange, 
  disabled = false,
  examCode,
  userId,
  autoSaveOnSubmit = false
}, ref) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [stream, setStream] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [timeLimitReached, setTimeLimitReached] = useState(false);
  const [streamDisabled, setStreamDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState({
    camera: null,
    microphone: null
  });

  const videoRef = useRef();
  const previewRef = useRef();
  const mountedRef = useRef(true);
  const durationTimerRef = useRef(null);

  // Duration limit: 2 minutes (120 seconds)
  const MAX_RECORDING_DURATION = 120;

  // Check browser support
  const isSupported = useCallback(() => {
    return !!(navigator.mediaDevices && 
              navigator.mediaDevices.getUserMedia && 
              window.MediaRecorder);
  }, []);

  // Request camera and microphone access
  const requestPermissions = useCallback(async () => {
    console.log('🎥 requestPermissions called', {
      streamDisabled,
      isSubmitting,
      isUploading,
      isSupported: isSupported(),
      mounted: mountedRef.current,
      hasExistingVideo: !!(answer?.videoBlob || answer?.videoUrl || answer?.videoPath)
    });
    
    if (streamDisabled) {
      console.log('❌ Stream access disabled due to time limit reached');
      setError('Recording time limit has been reached');
      return false;
    }
    
    if (isSubmitting) {
      console.log('❌ Stream access disabled during submission');
      setError('Cannot initialize camera during submission');
      return false;
    }
    
    if (isUploading) {
      console.log('❌ Stream access disabled during upload');
      setError('Cannot initialize camera during upload');
      return false;
    }
    
    if (!isSupported()) {
      const errorMsg = 'Video recording is not supported in this browser. Please use Chrome, Firefox, Safari, or Edge.';
      console.error('❌', errorMsg);
      setError(errorMsg);
      return false;
    }

    // CRITICAL: Clear any existing video when user clicks Initialize Camera
    // This allows users to re-record even if they have a previous video
    // This prevents users from being blocked from recording new videos
    if (answer?.videoBlob || answer?.videoUrl || answer?.videoPath) {
      console.log('🧹 Clearing existing video to allow new recording');
      
      // Clear video URL if it's a blob URL
      if (videoUrl && !videoUrl.startsWith('http')) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {
          // Ignore errors
        }
        setVideoUrl(null);
      }
      
      // Clear recorded chunks
      setRecordedChunks([]);
      
      // Clear answer object (but keep selectedScenario if it exists)
      onAnswerChange(question.id, {
        ...answer,
        videoBlob: null,
        videoChunks: null,
        videoUrl: null,
        videoPath: null,
        videoSize: null,
        videoType: null,
        // Keep selectedScenario if it exists
        selectedScenario: answer?.selectedScenario || null
      });
      
      // Clear sessionStorage
      const videoKey = `video_${examCode}_${question.id}`;
      sessionStorage.removeItem(videoKey);
      sessionStorage.removeItem(`${videoKey}_type`);
      sessionStorage.removeItem(`${videoKey}_timestamp`);
      sessionStorage.removeItem(`${videoKey}_exists`);
      sessionStorage.removeItem(`${videoKey}_size`);
    }

    try {
      console.log('📹 Requesting camera and microphone permissions...');
      setError(null);
      
      // Request media with explicit audio constraints to ensure audio is captured properly
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      });
      
      console.log('✅ Camera and microphone permissions granted');
      
      // Validate audio tracks are present and enabled
      const audioTracks = mediaStream.getAudioTracks();
      const videoTracks = mediaStream.getVideoTracks();
      
      console.log('📊 Stream tracks:', {
        audioTracks: audioTracks.length,
        videoTracks: videoTracks.length,
        audioEnabled: audioTracks.length > 0 && audioTracks[0].enabled,
        videoEnabled: videoTracks.length > 0 && videoTracks[0].enabled
      });
      
      // Check if audio track exists and is enabled
      if (audioTracks.length === 0) {
        console.warn('⚠️ No audio tracks found in stream');
        setError('Microphone access was denied or no microphone detected. Audio will not be recorded.');
        setPermissions({ camera: 'granted', microphone: 'denied' });
      } else if (!audioTracks[0].enabled) {
        console.warn('⚠️ Audio track is disabled');
        setError('Microphone is disabled. Please enable it in your browser settings.');
        setPermissions({ camera: 'granted', microphone: 'denied' });
      } else {
        console.log('✅ Audio track is enabled and ready');
        setPermissions({ camera: 'granted', microphone: 'granted' });
      }
      
      // Check if video track exists
      if (videoTracks.length === 0) {
        console.error('❌ No video tracks found in stream');
        setError('Camera access failed. Please check your camera permissions.');
        mediaStream.getTracks().forEach(track => track.stop());
        return false;
      }
      
      if (mountedRef.current) {
      setStream(mediaStream);
        setIsInitialized(true);
      
        // Set video source
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          // Mute video element to prevent audio feedback/echo during recording
          // Audio will still be recorded, but won't play back through speakers
          videoRef.current.muted = true;
          videoRef.current.volume = 0; // Also set volume to 0 for extra safety
          console.log('📹 Video stream attached to video element (muted to prevent echo)');
        } else {
          console.warn('⚠️ videoRef.current is null');
        }
        
      return true;
      } else {
        console.warn('⚠️ Component unmounted, stopping stream');
        mediaStream.getTracks().forEach(track => track.stop());
        return false;
      }
    } catch (err) {
      console.error('❌ Permission request failed:', err);
      if (mountedRef.current) {
        let errorMessage = 'Camera and microphone access is required for video recording. ';
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage += 'Please allow access in your browser settings and try again.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage += 'No camera or microphone found. Please connect a device and try again.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage += 'Camera or microphone is already in use by another application.';
        } else {
          errorMessage += `Error: ${err.message || 'Unknown error'}`;
        }
        setError(errorMessage);
        setPermissions({ camera: 'denied', microphone: 'denied' });
      }
      return false;
    }
  }, [streamDisabled, isSubmitting, isUploading, isSupported, answer, question.id, examCode, videoUrl, onAnswerChange]);

  // Start recording
  const startRecording = useCallback(() => {
    if (!stream || !isInitialized) {
      setError('Please initialize camera first');
        return;
    }

    // Clear any existing video when starting new recording
    if (videoUrl) {
      try {
        URL.revokeObjectURL(videoUrl);
      } catch (e) {
        // Ignore errors
      }
      setVideoUrl(null);
    }
    setRecordedChunks([]);
    
    // Clear old video from answer object (will be replaced with new recording)
    if (answer?.videoBlob) {
      onAnswerChange(question.id, {
        ...answer,
        videoBlob: null,
        videoChunks: null,
        videoUrl: null
      });
    }

    try {
      // Validate audio tracks before recording
      const audioTracks = stream.getAudioTracks();
      const videoTracks = stream.getVideoTracks();
      
      console.log('🎤 Pre-recording audio check:', {
        audioTracks: audioTracks.length,
        audioEnabled: audioTracks.length > 0 ? audioTracks[0].enabled : false,
        audioReady: audioTracks.length > 0 ? audioTracks[0].readyState : 'none',
        videoTracks: videoTracks.length
      });
      
      // Warn if audio is not available but continue recording video
      if (audioTracks.length === 0) {
        console.warn('⚠️ WARNING: No audio tracks available - video will be recorded without sound');
        setError('Warning: Microphone is not available. Video will be recorded without audio.');
      } else if (!audioTracks[0].enabled) {
        console.warn('⚠️ WARNING: Audio track is disabled - video will be recorded without sound');
        setError('Warning: Microphone is disabled. Video will be recorded without audio.');
        // Try to enable the audio track
        try {
          audioTracks[0].enabled = true;
          console.log('✅ Audio track enabled successfully');
        } catch (e) {
          console.error('❌ Failed to enable audio track:', e);
        }
      } else {
        console.log('✅ Audio track is enabled and ready for recording');
      }
      
      // Try to use best available codec, fallback to default if VP9 not supported
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
          console.warn('⚠️ VP9 and VP8 not supported, using default codec');
        }
      }
      
      // Create MediaRecorder with explicit audio settings
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000, // Ensure audio is recorded with good quality
        videoBitsPerSecond: 2500000 // Video bitrate
      });
      
      console.log('🎥 MediaRecorder created:', {
        mimeType: mimeType,
        hasAudio: audioTracks.length > 0,
        audioEnabled: audioTracks.length > 0 ? audioTracks[0].enabled : false
      });
      
      const chunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          console.log('📦 Data chunk received:', (event.data.size / 1024).toFixed(2), 'KB');
        }
      };

      // Add error handler for MediaRecorder
      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        setError(`Recording error: ${event.error?.message || 'Unknown error'}. Please try again.`);
      };

      mediaRecorder.onstop = async () => {
        // Verify audio was recorded by checking final audio track state
        const finalAudioTracks = stream.getAudioTracks();
        if (finalAudioTracks.length > 0) {
          console.log('🎤 Final audio track state:', {
            enabled: finalAudioTracks[0].enabled,
            readyState: finalAudioTracks[0].readyState,
            muted: finalAudioTracks[0].muted
          });
        }
        const originalBlob = new Blob(chunks, { type: 'video/webm' });
        console.log('📹 Original video size:', (originalBlob.size / 1024 / 1024).toFixed(2), 'MB');
        
        // STEP 1: Show video immediately with original blob (don't wait for compression)
        const originalUrl = URL.createObjectURL(originalBlob);
        setVideoUrl(originalUrl);
        setRecordedChunks([originalBlob]); // Store original blob temporarily
        
        // STEP 2: Save original blob immediately to answer object for instant persistence
        // This ensures video is available immediately when switching sections
        const initialVideoAnswerData = {
          videoBlob: originalBlob, // Store original blob first
          videoChunks: [originalBlob],
          videoUrl: originalUrl,
          videoSize: originalBlob.size,
          videoType: originalBlob.type,
          timestamp: Date.now(),
          selectedScenario: answer?.selectedScenario || null,
          isCompressing: true // Flag to indicate compression is in progress
        };
        
        // Save immediately so video persists across section switches
        onAnswerChange(question.id, initialVideoAnswerData);
        console.log('💾 Video saved immediately (original):', question.id, (originalBlob.size / 1024 / 1024).toFixed(2), 'MB');
        
        // STEP 3: Compress in background (non-blocking, async)
        // This happens after video is already shown and saved
        compressVideo(originalBlob)
          .then(compressedBlob => {
            // Check if component is still mounted before updating state
            if (!mountedRef.current) {
              console.log('⚠️ Component unmounted, skipping compression update');
              return;
            }
            
            // Validate compressed blob
            if (!compressedBlob || compressedBlob.size === 0) {
              console.warn('⚠️ Compressed blob is empty or invalid, keeping original');
              const updatedAnswerData = {
                ...initialVideoAnswerData,
                isCompressing: false
              };
              if (mountedRef.current) {
                onAnswerChange(question.id, updatedAnswerData);
              }
              return;
            }
            
            // Only use compressed if it's actually smaller (at least 5% reduction)
            if (compressedBlob.size < originalBlob.size * 0.95) {
              const compressionRatio = ((1 - compressedBlob.size / originalBlob.size) * 100).toFixed(1);
              console.log('✅ Video compressed:', (compressedBlob.size / 1024 / 1024).toFixed(2), 'MB', `(${compressionRatio}% reduction)`);
              
              // Revoke old URL
              if (originalUrl && mountedRef.current) {
                try {
                  URL.revokeObjectURL(originalUrl);
                } catch (e) {
                  // Ignore errors
                }
              }
              
              // Create new URL from compressed blob
              const compressedUrl = URL.createObjectURL(compressedBlob);
              
              if (mountedRef.current) {
                setVideoUrl(compressedUrl);
                setRecordedChunks([compressedBlob]);
                
                // Update answer object with compressed version
                const compressedVideoAnswerData = {
                  videoBlob: compressedBlob, // Replace with compressed blob
                  videoChunks: [compressedBlob],
                  videoUrl: compressedUrl,
                  videoSize: compressedBlob.size,
                  videoType: compressedBlob.type,
                  timestamp: Date.now(),
                  selectedScenario: answer?.selectedScenario || null,
                  isCompressing: false
                };
                
                onAnswerChange(question.id, compressedVideoAnswerData);
                console.log('💾 Video updated with compressed version:', question.id, (compressedBlob.size / 1024 / 1024).toFixed(2), 'MB');
              } else {
                // Component unmounted, cleanup
                try {
                  URL.revokeObjectURL(compressedUrl);
                } catch (e) {
                  // Ignore errors
                }
              }
            } else {
              console.log('ℹ️ Compression did not reduce size significantly, keeping original');
              // Mark compression as complete even if we kept original
              const updatedAnswerData = {
                ...initialVideoAnswerData,
                isCompressing: false
              };
              if (mountedRef.current) {
                onAnswerChange(question.id, updatedAnswerData);
              }
            }
          })
          .catch(error => {
            console.warn('⚠️ Video compression failed, keeping original:', error);
            // Mark compression as complete, keep original
            if (mountedRef.current) {
              const updatedAnswerData = {
                ...initialVideoAnswerData,
                isCompressing: false
              };
              onAnswerChange(question.id, updatedAnswerData);
            }
          });
        
        // Also try to save to sessionStorage for quick restore (if small enough)
        // Use original blob for sessionStorage (compression happens in background)
        const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB limit
        try {
          if (originalBlob.size > MAX_STORAGE_SIZE) {
            console.log('📦 Video too large for sessionStorage, but saved to answer object:', (originalBlob.size / 1024 / 1024).toFixed(2), 'MB');
            // Just save a flag that video exists
            const videoKey = `video_${examCode}_${question.id}`;
            sessionStorage.setItem(`${videoKey}_exists`, 'true');
            sessionStorage.setItem(`${videoKey}_timestamp`, Date.now().toString());
            sessionStorage.setItem(`${videoKey}_size`, originalBlob.size.toString());
            return;
          }
          
          const videoKey = `video_${examCode}_${question.id}`;
          const reader = new FileReader();
          reader.onloadend = () => {
            try {
              const base64data = reader.result;
              // Check estimated size (base64 is ~33% larger than binary)
              const estimatedSize = base64data.length;
              if (estimatedSize > MAX_STORAGE_SIZE) {
                console.log('📦 Base64 video too large for sessionStorage, but saved to answer object');
                sessionStorage.setItem(`${videoKey}_exists`, 'true');
                sessionStorage.setItem(`${videoKey}_timestamp`, Date.now().toString());
                sessionStorage.setItem(`${videoKey}_size`, originalBlob.size.toString());
                return;
              }
              
              sessionStorage.setItem(videoKey, base64data);
              sessionStorage.setItem(`${videoKey}_type`, originalBlob.type);
              sessionStorage.setItem(`${videoKey}_timestamp`, Date.now().toString());
              console.log('💾 Video also saved to sessionStorage:', videoKey);
            } catch (error) {
              // Handle quota exceeded error gracefully
              if (error.name === 'QuotaExceededError') {
                console.warn('⚠️ Storage quota exceeded, but video saved to answer object');
                sessionStorage.setItem(`${videoKey}_exists`, 'true');
                sessionStorage.setItem(`${videoKey}_timestamp`, Date.now().toString());
                sessionStorage.setItem(`${videoKey}_size`, originalBlob.size.toString());
              } else {
                throw error;
              }
            }
          };
          reader.onerror = () => {
            console.error('❌ Failed to read video file for sessionStorage, but saved to answer object');
            // Save reference only
            const videoKey = `video_${examCode}_${question.id}`;
            sessionStorage.setItem(`${videoKey}_exists`, 'true');
            sessionStorage.setItem(`${videoKey}_timestamp`, Date.now().toString());
          };
          reader.readAsDataURL(originalBlob);
        } catch (error) {
          console.error('❌ Failed to save video to sessionStorage, but saved to answer object:', error);
          // Save reference only as fallback
          const videoKey = `video_${examCode}_${question.id}`;
          try {
            sessionStorage.setItem(`${videoKey}_exists`, 'true');
            sessionStorage.setItem(`${videoKey}_timestamp`, Date.now().toString());
          } catch (e) {
            console.error('❌ Failed to save video reference:', e);
          }
        }
      };
      
      // Ensure video element is muted before starting recording to prevent echo
      if (videoRef.current) {
        videoRef.current.muted = true;
        videoRef.current.volume = 0;
        console.log('🔇 Video element muted to prevent audio feedback during recording');
      }

      // Start recording with error handling and data collection interval
      try {
        mediaRecorder.start(1000); // Request data every 1 second for better reliability
        console.log('🎥 Recording started:', {
          state: mediaRecorder.state,
          mimeType: mimeType,
          hasAudio: audioTracks.length > 0,
          audioEnabled: audioTracks.length > 0 ? audioTracks[0].enabled : false
        });
      setMediaRecorder(mediaRecorder);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);
      setError(null);
      } catch (startError) {
        console.error('❌ Failed to start MediaRecorder:', startError);
        setError(`Failed to start recording: ${startError.message}. Please try again.`);
        // Clean up on failure
        stream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
        return;
      }
      
      // Clear any existing timer
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
      
      // Start duration timer
      durationTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          // Stop at 2 minutes automatically
          if (newDuration >= MAX_RECORDING_DURATION) {
            setTimeLimitReached(true);
            if (durationTimerRef.current) {
              clearInterval(durationTimerRef.current);
              durationTimerRef.current = null;
            }
            // Stop recording (this will trigger onstop and show preview automatically)
            setTimeout(() => {
              if (mediaRecorder && mediaRecorder.state !== 'inactive') {
                mediaRecorder.stop();
                setIsRecording(false);
                setIsPaused(false);
              }
            }, 0);
          }
          return newDuration;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Recording start failed:', err);
      setError('Failed to start recording. Please try again.');
    }
  }, [stream, isInitialized]);

  // Ensure video element is muted during recording to prevent echo/feedback
  useEffect(() => {
    if (videoRef.current) {
      if (isRecording) {
        // Mute video during recording to prevent audio feedback
        videoRef.current.muted = true;
        videoRef.current.volume = 0;
        console.log('🔇 Video muted during recording to prevent echo');
      }
      // Note: Keep muted even when not recording to prevent accidental audio playback
      // Only unmute when user explicitly plays the recorded video preview
    }
  }, [isRecording]);

  // Stop recording
  const stopRecording = useCallback(() => {
    // Clear duration timer
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  }, [mediaRecorder]);

  // Toggle pause/resume
  const togglePause = useCallback(() => {
    if (!mediaRecorder) return;
    
      if (isPaused) {
        mediaRecorder.resume();
        setIsPaused(false);
      } else {
        mediaRecorder.pause();
        setIsPaused(true);
    }
  }, [mediaRecorder, isPaused]);

  // Upload video to Firebase Storage
  const uploadVideo = useCallback(async (isAutoSave = false) => {
    if (!recordedChunks.length) {
      setError('No video recorded to upload.');
      return null;
    }

    return new Promise((resolve, reject) => {
      try {
        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        const blob = new Blob(recordedChunks, { type: 'video/webm' });
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const fileName = `video_${timestamp}_${randomId}.webm`;
        
        const userEmail = JSON.parse(localStorage.getItem('user'))?.email || 
                         sessionStorage.getItem('checkemail') || 
                         'anonymous';
        
        const storageRef = firebase.storage().ref(`submissions/${examCode}/${question.id}/${fileName}`);
        
        const uploadTask = storageRef.put(blob);
        
        uploadTask.on('state_changed',
          (snapshot) => {
            if (mountedRef.current) {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(Math.round(progress));
            }
          },
          (error) => {
            console.error('Upload failed:', error);
            if (mountedRef.current) {
              setError('Failed to upload video. Please try again.');
              setIsUploading(false);
            }
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
              const storagePath = `${examCode}/${question.id}/${fileName}`;
              
              // CRITICAL: Clear all blob-related fields when video is uploaded to Firebase
              // Only keep videoPath (Firebase Storage path) and remove blob URLs
              const videoAnswer = {
                videoPath: storagePath, // Firebase Storage path (e.g., "examCode/questionId/fileName")
                selectedScenario: answer?.selectedScenario || null,
                // Explicitly remove blob-related fields
                videoUrl: null, // Clear blob URL - use videoPath instead
                videoBlob: null, // Clear blob - video is now in Firebase Storage
                videoChunks: null, // Clear chunks - video is now in Firebase Storage
                videoSize: null, // Optional: can keep if needed
                videoType: null, // Optional: can keep if needed
                isCompressing: false // Clear compression flag
              };
              
              console.log('✅ Video uploaded (auto-save) - blob URLs cleared, using videoPath:', storagePath);
              
              // Clear sessionStorage after successful upload
              const videoKey = `video_${examCode}_${question.id}`;
              sessionStorage.removeItem(videoKey);
              sessionStorage.removeItem(`${videoKey}_type`);
              sessionStorage.removeItem(`${videoKey}_timestamp`);
              sessionStorage.removeItem(`${videoKey}_exists`);
              sessionStorage.removeItem(`${videoKey}_size`);
              
              if (mountedRef.current) {
                onAnswerChange(question.id, videoAnswer);
                setUploadProgress(100);
                setIsUploading(false);
                
                // Clear recorded chunks and video URL after upload
                setRecordedChunks([]);
                if (videoUrl) {
                  URL.revokeObjectURL(videoUrl);
                  setVideoUrl(null);
                }
                
                if (!isAutoSave) {
                  swal({
                    title: "Video Uploaded Successfully!",
                    text: "Your video has been saved and will be submitted with your assessment.",
                    icon: "success",
                    button: "Continue",
                    timer: 3000
                  });
                }
              }
              
              resolve(videoAnswer);
            } catch (error) {
              console.error('Failed to get download URL:', error);
              if (mountedRef.current) {
                setError('Failed to save video. Please try again.');
                setIsUploading(false);
              }
              reject(error);
            }
          }
        );
      } catch (error) {
        console.error('Upload error:', error);
        if (mountedRef.current) {
          setError('Failed to upload video. Please try again.');
          setIsUploading(false);
        }
        reject(error);
      }
    });
  }, [recordedChunks, question.id, examCode, answer?.selectedScenario, onAnswerChange]);

  // Expose save function to parent component
  useImperativeHandle(ref, () => ({
    saveVideoForSubmission: async () => {
      let chunksToUpload = recordedChunks;
      
      // If no chunks in memory, try to restore from answer object first (for large videos)
      if (!chunksToUpload || chunksToUpload.length === 0) {
        if (answer?.videoBlob && answer.videoBlob instanceof Blob) {
          // Restore from answer object
          if (answer.videoChunks && Array.isArray(answer.videoChunks) && answer.videoChunks.length > 0) {
            chunksToUpload = answer.videoChunks;
          } else {
            chunksToUpload = [answer.videoBlob];
          }
          // Update state for UI consistency
          setRecordedChunks(chunksToUpload);
          console.log('Restored video chunks from answer object for submission');
        } else {
          // Fallback: Try to restore from sessionStorage
        const videoKey = `video_${examCode}_${question.id}`;
        const savedVideo = sessionStorage.getItem(videoKey);
        if (!savedVideo) {
          return null;
        }
        // Restore chunks from sessionStorage
        try {
          const videoType = sessionStorage.getItem(`${videoKey}_type`) || 'video/webm';
          const byteCharacters = atob(savedVideo.split(',')[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: videoType });
          chunksToUpload = [blob];
          // Update state for UI consistency
          setRecordedChunks(chunksToUpload);
            console.log('Restored video chunks from sessionStorage for submission');
        } catch (error) {
          console.error('Failed to restore chunks from sessionStorage:', error);
          return null;
          }
        }
      }
      
      if (!chunksToUpload || chunksToUpload.length === 0) {
        return null;
      }
      
      setIsSubmitting(true);
      setStreamDisabled(true);
        
      try {
        // Create a temporary upload function with the chunks
        const blob = new Blob(chunksToUpload, { type: 'video/webm' });
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const fileName = `video_${timestamp}_${randomId}.webm`;
        
        const userEmail = JSON.parse(localStorage.getItem('user'))?.email || 
                         sessionStorage.getItem('checkemail') || 
                         'anonymous';
        
        const storageRef = firebase.storage().ref(`submissions/${examCode}/${question.id}/${fileName}`);
        const uploadTask = storageRef.put(blob);
        
        const videoAnswer = await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              if (mountedRef.current) {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(Math.round(progress));
              }
            },
            (error) => {
              console.error('Upload failed:', error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
                const storagePath = `${examCode}/${question.id}/${fileName}`;
                
                // CRITICAL: Clear all blob-related fields when video is uploaded to Firebase
                // Only keep videoPath (Firebase Storage path) and remove blob URLs
                const videoAnswerData = {
                  videoPath: storagePath, // Firebase Storage path (e.g., "examCode/questionId/fileName")
                  selectedScenario: answer?.selectedScenario || null,
                  // Explicitly remove blob-related fields
                  videoUrl: null, // Clear blob URL - use videoPath instead
                  videoBlob: null, // Clear blob - video is now in Firebase Storage
                  videoChunks: null, // Clear chunks - video is now in Firebase Storage
                  videoSize: null, // Optional: can keep if needed
                  videoType: null, // Optional: can keep if needed
                  isCompressing: false // Clear compression flag
                };
                
                // Clear sessionStorage after successful upload
                const videoKey = `video_${examCode}_${question.id}`;
                sessionStorage.removeItem(videoKey);
                sessionStorage.removeItem(`${videoKey}_type`);
                sessionStorage.removeItem(`${videoKey}_timestamp`);
                sessionStorage.removeItem(`${videoKey}_exists`);
                sessionStorage.removeItem(`${videoKey}_size`);
                
                console.log('✅ Video uploaded - blob URLs cleared, using videoPath:', storagePath);
                resolve(videoAnswerData);
              } catch (error) {
                reject(error);
              }
            }
          );
        });
        
        if (mountedRef.current) {
          onAnswerChange(question.id, videoAnswer);
          setRecordedChunks([]);
          if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
          }
        }
        
        console.log('Video uploaded successfully for submission:', videoAnswer);
        return videoAnswer;
      } catch (error) {
        console.error('Video upload failed:', error);
        throw error;
      } finally {
        setIsSubmitting(false);
        setUploadProgress(0);
      }
    },
    hasUnsavedVideo: () => {
      // Check recordedChunks, answer object, and sessionStorage
      const videoKey = `video_${examCode}_${question.id}`;
      const hasChunks = recordedChunks.length > 0;
      const hasVideoInAnswer = answer?.videoBlob && answer.videoBlob instanceof Blob;
      const hasSavedVideo = sessionStorage.getItem(videoKey) !== null || 
                            sessionStorage.getItem(`${videoKey}_exists`) === 'true';
      
      // CRITICAL: Video needs upload if:
      // 1. Has chunks/blob but no videoPath (Firebase Storage path), OR
      // 2. Has blob URL (needs to be uploaded to Firebase)
      const hasVideoToUpload = hasChunks || hasVideoInAnswer || hasSavedVideo;
      const hasBlobUrl = answer?.videoUrl && answer.videoUrl.startsWith('blob:');
      const hasVideoPath = !!answer?.videoPath; // Firebase Storage path
      
      // Return true if we have video to upload AND no valid Firebase Storage path
      return hasVideoToUpload && !hasVideoPath;
    },
    isUploading: () => isUploading
  }), [recordedChunks, answer, question.id, examCode, isUploading, onAnswerChange, videoUrl]);

  // Restore video from answer object or sessionStorage on mount
  // IMPORTANT: Only clear video data on actual page reload, not on section switches
  useEffect(() => {
    // Check if video is already uploaded to Firebase (has videoPath or videoUrl from Firebase)
    if (answer?.videoPath || (answer?.videoUrl && answer.videoUrl.startsWith('http'))) {
      // Video is uploaded - clear any local state
      if (videoUrl && !videoUrl.startsWith('http')) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {}
        setVideoUrl(null);
      }
      setRecordedChunks([]);
      return;
    }
    
    // Check if answer.videoBlob exists but is not a valid Blob (lost on page reload)
    // This is the only reliable way to detect page reload - Blobs can't be serialized
    if (answer?.videoBlob && !(answer.videoBlob instanceof Blob)) {
      console.log('🧹 Invalid videoBlob detected (lost on page reload) - clearing video data');
      
      // Clear sessionStorage
      const videoKey = `video_${examCode}_${question.id}`;
      sessionStorage.removeItem(videoKey);
      sessionStorage.removeItem(`${videoKey}_type`);
      sessionStorage.removeItem(`${videoKey}_timestamp`);
      sessionStorage.removeItem(`${videoKey}_exists`);
      sessionStorage.removeItem(`${videoKey}_size`);
      
      // Clear answer object
      onAnswerChange(question.id, {
        ...answer,
        videoBlob: null,
        videoChunks: null,
        videoUrl: null,
        videoSize: null,
        videoType: null
      });
      
      // Clear local state
      if (videoUrl && !videoUrl.startsWith('http')) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {}
        setVideoUrl(null);
      }
      setRecordedChunks([]);
      return;
    }
    
    // Only restore if we have a valid Blob in the answer object (same session, not page reload)
    if (answer?.videoBlob && answer.videoBlob instanceof Blob) {
      try {
        // Revoke old URL if exists
        if (videoUrl && !videoUrl.startsWith('http')) {
          try {
            URL.revokeObjectURL(videoUrl);
          } catch (e) {
            // Ignore errors
          }
        }
        
        const url = URL.createObjectURL(answer.videoBlob);
        setVideoUrl(url);
        
        // Restore chunks if available, otherwise create from blob
        if (answer.videoChunks && Array.isArray(answer.videoChunks) && answer.videoChunks.length > 0) {
          setRecordedChunks(answer.videoChunks);
        } else {
          setRecordedChunks([answer.videoBlob]);
        }
        
        console.log('✅ Video restored from answer object (same session):', question.id, (answer.videoBlob.size / 1024 / 1024).toFixed(2), 'MB');
        return;
      } catch (error) {
        console.error('❌ Failed to restore video from answer object:', error);
      }
    }
    
    // Fallback: Try to restore from sessionStorage (for smaller videos in same session)
      const videoKey = `video_${examCode}_${question.id}`;
      const savedVideo = sessionStorage.getItem(videoKey);
      
      if (savedVideo) {
        try {
          // Convert base64 back to blob
          const videoType = sessionStorage.getItem(`${videoKey}_type`) || 'video/webm';
          const byteCharacters = atob(savedVideo.split(',')[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: videoType });
          
          // Create chunks array from blob (for compatibility)
          const chunks = [blob];
          const url = URL.createObjectURL(blob);
          
          setVideoUrl(url);
          setRecordedChunks(chunks);
        console.log('✅ Video restored from sessionStorage:', videoKey);
        } catch (error) {
        console.error('❌ Failed to restore video from sessionStorage:', error);
          // Clear invalid data
          sessionStorage.removeItem(videoKey);
          sessionStorage.removeItem(`${videoKey}_type`);
          sessionStorage.removeItem(`${videoKey}_timestamp`);
          sessionStorage.removeItem(`${videoKey}_exists`);
          sessionStorage.removeItem(`${videoKey}_size`);
        }
    } else {
      // No video to restore - clear any stale state
      if (videoUrl && !videoUrl.startsWith('http')) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {
          // Ignore errors
        }
        setVideoUrl(null);
      }
      setRecordedChunks([]);
    }
  }, [examCode, question.id, answer?.videoUrl, answer?.videoPath, answer?.videoBlob, answer?.videoChunks, onAnswerChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      // Don't revoke videoUrl if we want to preserve it for navigation
      // Only revoke if component is truly unmounting and video is uploaded
      if (videoUrl && !answer?.videoUrl && !answer?.videoPath) {
        // Keep the URL for now - it will be cleaned up on next mount or on upload
      }
    };
  }, [stream, videoUrl, answer?.videoUrl, answer?.videoPath]);

  // Format duration for display
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="video-recorder-container">
      {/* Instructions */}
      <div className="video-recorder-instructions">
        <div className="video-recorder-instructions-header">
          📹 Video Record
            </div>
        <div className="video-recorder-instructions-text">
          Record your response to the question. Maximum duration: 2 minutes.
          {permissions.microphone === 'denied' && (
            <div style={{ 
              marginTop: '8px', 
              padding: '8px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107', 
              borderRadius: '4px',
              fontSize: '12px',
              color: '#856404'
            }}>
              ⚠️ Warning: Microphone is not available. Video will be recorded without audio.
            </div>
          )}
            </div>
            </div>

      {/* Timer Display */}
      {isRecording && (
        <div className="video-recorder-timer-wrapper">
          {/* Timer on Left */}
          <div className="video-recorder-timer">
            <span className="video-recorder-timer-text">
              {isPaused ? 'PAUSED' : 'RECORDING'} - {formatDuration(recordingDuration)}
            </span>
          </div>
          
          {/* Warning Timer on Right */}
          {recordingDuration >= 110 && (
            <div className="video-recorder-warning-timer">
              <span>⚠️</span>
              <span>
                {MAX_RECORDING_DURATION - recordingDuration}s remaining
              </span>
            </div>
          )}
        </div>
      )}

      {/* Video Preview Container */}
      <div className="video-recorder-preview-container">
            {!isInitialized ? (
          <div style={{ textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📹</div>
            <div style={{ fontSize: '12px' }}>Ready to Record</div>
          </div>
        ) : (
          <>
                    <video
                      ref={videoRef}
                      autoPlay
              playsInline
                      muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
              borderRadius: '4px'
            }}
          />
            {/* Audio Status Indicator */}
            {isInitialized && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: permissions.microphone === 'granted' ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                zIndex: 10
              }}>
                <Mic style={{ fontSize: '16px' }} />
                <span>{permissions.microphone === 'granted' ? 'Audio On' : 'Audio Off'}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Controls */}
      <div className="video-recorder-controls">
        {!isInitialized ? (
          <button
            type="button"
            className="video-recorder-button video-recorder-start-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🎥 Initialize Camera button clicked');
              requestPermissions();
            }}
            disabled={disabled || streamDisabled || isSubmitting || isUploading}
          >
            Initialize Camera
          </button>
        ) : !isRecording && !videoUrl ? (
          <button
            type="button"
            className="video-recorder-button video-recorder-start-btn"
            onClick={startRecording}
            disabled={disabled || !isInitialized}
          >
            Start Recording
          </button>
        ) : isRecording ? (
          <>
            <button
              type="button"
              className="video-recorder-button video-recorder-stop-btn"
              onClick={stopRecording}
              disabled={disabled}
            >
              Stop Recording
            </button>
            <button
              type="button"
              className="video-recorder-button video-recorder-pause-btn"
              onClick={togglePause}
              disabled={disabled}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="video-recorder-button video-recorder-view-btn"
              onClick={() => {
                if (previewRef.current) {
                  previewRef.current.play();
                }
              }}
              disabled={disabled}
            >
              Preview
          </button>
        )}
      </div>

      {/* Success Message */}
        {answer?.videoUrl && (
        <div className="video-recorder-success">
            <CheckCircle style={{ color: '#28a745', fontSize: 16 }} />
          <span style={{ color: '#155724', fontWeight: 600, fontSize: 12 }}>
            Video Saved Successfully!
          </span>
        </div>
      )}

      {/* Video Preview */}
      {videoUrl && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ 
            marginBottom: '4px', 
            color: '#6c757d', 
            fontWeight: 600, 
            textAlign: 'center',
            fontSize: '11px'
          }}>
            📹 Preview
          </div>
          <div style={{ textAlign: 'center' }}>
            <video
              ref={previewRef}
              src={videoUrl}
              controls
              style={{
                width: '100%',
                maxWidth: '500px',
                height: 'auto',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="video-recorder-progress">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <CircularProgress size={16} />
            <span style={{ color: '#495057', fontWeight: 600, fontSize: '12px' }}>
              Uploading {uploadProgress}%
            </span>
          </div>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            style={{
              height: '4px',
              borderRadius: '2px',
              backgroundColor: '#e9ecef'
            }}
          />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="video-recorder-error">
          {error}
        </div>
      )}
    </div>
  );
});

VideoRecorder.displayName = 'VideoRecorder';

export default VideoRecorder;